import { Injectable, Injector, OnDestroy } from '@angular/core';
import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ApiV1Service } from './api-v1.service';
import { ApiV2Service } from './api-v2.service';

export interface ActivityTrackOptions {
  module_name?: string;
  page_name?: string;
  screen_name?: string;
  description?: string;
  page_route?: string;
  reference_type?: string;
  reference_id?: string | number;
  session_status?: string;
  login_at?: string;
  logout_at?: string;
  activity_duration_sec?: number;
  action?: string;
  /** When true, flush queue immediately after enqueue (logout / leave). */
  flushNow?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ActivityTrackingService implements OnDestroy {
  private readonly LOGIN_AT_KEY = 'plus_activity_login_at';
  private readonly LEAVE_AT_KEY = 'plus_activity_leave_at';
  private readonly EXCLUDED_ROUTES = ['/auth', '/login', '/register', '/forgot-password'];
  private readonly FLUSH_MS = 5000;
  private readonly FLUSH_AT_SIZE = 20;
  private readonly MAX_QUEUE = 200;

  private menuIndex: Array<{
    module_name: string;
    page_name: string;
    state: string;
  }> = [];
  private routerSub?: Subscription;
  private initialized = false;
  private lastTrackedRoute = '';
  private visibilityBound = false;
  private unloadBound = false;
  private queue: any[] = [];
  private flushTimer: any = null;
  private flushing = false;
  private flushPromise: Promise<void> | null = null;
  /** Bypasses HTTP interceptors — avoids circular DI / login break. */
  private readonly rawHttp: HttpClient;

  constructor(
    private router: Router,
    private injector: Injector,
    httpBackend: HttpBackend
  ) {
    this.rawHttp = new HttpClient(httpBackend);
  }

  private get apiV1(): ApiV1Service {
    return this.injector.get(ApiV1Service);
  }

  private get apiV2(): ApiV2Service {
    return this.injector.get(ApiV2Service);
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    if (this.visibilityBound) {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.visibilityBound = false;
    }
    if (this.unloadBound) {
      window.removeEventListener('pagehide', this.onPageHide);
      this.unloadBound = false;
    }
    this.flush();
  }

  /** Register MENUITEMS for module/page enrichment. */
  setMenuItems(menuItems: any[]): void {
    const index: Array<{ module_name: string; page_name: string; state: string }> = [];
    (menuItems || []).forEach((group) => {
      const moduleName = group?.label || '';
      (group?.main || []).forEach((item: any) => {
        if (item?.state) {
          index.push({
            module_name: moduleName,
            page_name: item.name || item.state,
            state: String(item.state).replace(/^\/+/, ''),
          });
        }
        (item?.children || []).forEach((child: any) => {
          if (child?.state) {
            const childState = item.state
              ? `${String(item.state).replace(/\/+$/, '')}/${String(child.state).replace(/^\/+/, '')}`
              : String(child.state).replace(/^\/+/, '');
            index.push({
              module_name: moduleName,
              page_name: child.name || child.state,
              state: childState,
            });
          }
        });
      });
    });
    this.menuIndex = index.sort((a, b) => b.state.length - a.state.length);
  }

  initialize(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    this.routerSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.onNavigation(e.urlAfterRedirects || e.url));

    if (!this.visibilityBound) {
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      this.visibilityBound = true;
    }
    if (!this.unloadBound) {
      window.addEventListener('pagehide', this.onPageHide);
      this.unloadBound = true;
    }

    this.flushTimer = setInterval(() => this.flush(), this.FLUSH_MS);
  }

  /** Resolve menu metadata for current/given route (used by API interceptor). */
  resolveRouteMeta(url?: string): { module_name: string; page_name: string; page_route: string } {
    const path = this.normalizePath(url || this.router.url || '');
    const menu = this.resolveMenu(path);
    return {
      module_name: menu?.module_name || '',
      page_name: menu?.page_name || path || '',
      page_route: path || this.router.url || '',
    };
  }

  /** Enqueue activity (batched). Never throws to callers. */
  track(action: string, options: ActivityTrackOptions = {}): void {
    try {
      try {
        if (
          sessionStorage.getItem('account_switch_in_progress') &&
          String(action || '').toUpperCase() !== 'ACCOUNT_SWITCH' &&
          String(action || '').toUpperCase() !== 'LOGIN'
        ) {
          return;
        }
      } catch {
        /* ignore */
      }
      const v1 = this.apiV2.currentUserValue?.v1_data;
      if (!v1?.token && action !== 'LOGIN') {
        if (!v1?.login_session_id && !this.apiV2.currentUserValue) {
          return;
        }
      }

      const userAgent =
        typeof navigator !== 'undefined' && navigator.userAgent
          ? navigator.userAgent
          : '';
      const src = this.apiV1.normalizeLoginSrc(
        v1?.src || this.apiV1.getLoginSrc()
      );
      const loginAt =
        options.login_at ||
        sessionStorage.getItem(this.LOGIN_AT_KEY) ||
        undefined;
      const routeMeta = this.resolveRouteMeta(options.page_route);

      const payload: any = {
        action: String(action || '').toUpperCase(),
        login_session_id: v1?.login_session_id || null,
        username: v1?.email || this.apiV2.currentUserValue?.email || '',
        employee_name: v1?.name || this.apiV2.currentUserValue?.first_name || '',
        role_name: v1?.role?.name || '',
        module_name: options.module_name || routeMeta.module_name || '',
        page_name: options.page_name || routeMeta.page_name || '',
        screen_name: options.screen_name || '',
        description: options.description || '',
        page_route: options.page_route || routeMeta.page_route || '',
        reference_type: options.reference_type || '',
        reference_id:
          options.reference_id !== undefined && options.reference_id !== null
            ? String(options.reference_id)
            : '',
        user_agent: userAgent,
        src,
        session_status: options.session_status || 'Active',
        login_at: loginAt || '',
        logout_at: options.logout_at || '',
        activity_duration_sec:
          options.activity_duration_sec !== undefined
            ? options.activity_duration_sec
            : null,
      };

      if (!payload.action || payload.action.length < 2) {
        return;
      }

      if (this.queue.length >= this.MAX_QUEUE) {
        this.queue.shift();
      }
      this.queue.push(payload);

      if (options.flushNow || this.queue.length >= this.FLUSH_AT_SIZE) {
        this.flush();
      }
    } catch {
      /* never impact host flows */
    }
  }

  /** Flush queued activities via batch API (raw HttpClient — no interceptors). */
  flush(): Promise<void> {
    if (this.flushing || !this.queue.length) {
      return this.flushPromise || Promise.resolve();
    }
    try {
      if (sessionStorage.getItem('account_switch_in_progress')) {
        return Promise.resolve();
      }
    } catch {
      /* ignore */
    }
    let v1Token = '';
    try {
      v1Token = this.apiV2.currentUserValue?.v1_data?.token || '';
      if (!v1Token) {
        const stored = JSON.parse(localStorage.getItem('currentUser') || 'null');
        v1Token = stored?.v1_data?.token || '';
      }
    } catch {
      v1Token = '';
    }
    if (!v1Token) {
      return Promise.resolve();
    }

    const batch = this.queue.splice(0, 100);
    this.flushing = true;
    const headers = new HttpHeaders({
      Authtoken: `Bearer ${v1Token}`,
    });
    const request = new Promise<void>((resolve) => {
      this.rawHttp
        .post(
          environment.V1_apiUrl + '/other/logactivitybatch',
          { activities: batch },
          { headers }
        )
        .subscribe({
          next: () => {
            this.flushing = false;
            resolve();
            if (this.queue.length >= this.FLUSH_AT_SIZE) {
              this.flush();
            }
          },
          error: () => {
            this.flushing = false;
            // Drop failed batch (e.g. session closed mid-switch) — do not retry loop
            this.queue = [];
            resolve();
          },
        });
    });
    this.flushPromise = request;
    request.then(() => {
      if (this.flushPromise === request) {
        this.flushPromise = null;
      }
    });
    return request;
  }

  trackLogin(): void {
    const now = this.nowSql();
    try {
      sessionStorage.setItem(this.LOGIN_AT_KEY, now);
      sessionStorage.removeItem(this.LEAVE_AT_KEY);
    } catch {
      /* ignore */
    }
    this.track('LOGIN', {
      description: 'User logged in',
      session_status: 'Active',
      login_at: now,
      page_route: this.router.url || '/auth',
      page_name: 'Login',
      module_name: 'auth',
      flushNow: true,
    });
  }

  /**
   * Log logout from frontend only (does not call /auth1/logout).
   * Call immediately before existing autologout(); await before redirecting.
   * @param logoutAtSql optional capped time for IDLE_3HR (last activity + 3h)
   */
  async trackLogout(logoutType: string = 'MANUAL', logoutAtSql?: string): Promise<void> {
    const type = String(logoutType || 'MANUAL').toUpperCase();
    let action = 'LOGOUT';
    let sessionStatus = 'Logged Out';
    let description = 'User logged out';

    if (type === 'IDLE_3HR') {
      action = 'AUTO_LOGOUT';
      sessionStatus = 'Auto Logged Out';
      description = 'User auto logged out after inactivity';
    } else if (type === 'TOKEN_FAIL') {
      action = 'AUTO_LOGOUT';
      sessionStatus = 'Auto Logged Out';
      description = 'User logged out due to token failure';
    } else if (type === 'ACCOUNT_SWITCH') {
      action = 'ACCOUNT_SWITCH';
      sessionStatus = 'Switched';
      description = 'User switched account';
    }

    this.track(action, {
      description,
      session_status: sessionStatus,
      logout_at: logoutAtSql || this.nowSql(),
      page_route: this.router.url || '',
      flushNow: true,
    });
    await this.flush();
    // If another batch was already in flight, the logout event was queued
    // behind it; flush once more so it is sent before session teardown.
    await this.flush();

    try {
      sessionStorage.removeItem(this.LOGIN_AT_KEY);
      sessionStorage.removeItem(this.LEAVE_AT_KEY);
    } catch {
      /* ignore */
    }
  }

  /** Log alias/account switch after new V1 session is ready. */
  trackAccountSwitch(options: {
    fromEmail?: string;
    toEmail?: string;
    fromId?: string | number;
    toId?: string | number;
  } = {}): void {
    const fromLabel = options.fromEmail || String(options.fromId || 'previous');
    const toLabel = options.toEmail || String(options.toId || 'next');
    const now = this.nowSql();
    try {
      sessionStorage.setItem(this.LOGIN_AT_KEY, now);
      sessionStorage.removeItem(this.LEAVE_AT_KEY);
    } catch {
      /* ignore */
    }
    this.track('ACCOUNT_SWITCH', {
      description: `Switched account from ${fromLabel} to ${toLabel}`,
      session_status: 'Active',
      login_at: now,
      page_route: this.router.url || '',
      page_name: 'Account Switch',
      module_name: 'auth',
      reference_type: 'account_switch',
      reference_id: `${fromLabel}->${toLabel}`,
      flushNow: true,
    });
  }

  trackSessionTimeoutWarning(): void {
    this.track('SESSION_TIMEOUT_WARNING', {
      description: 'Session timeout warning shown',
      session_status: 'Active',
      page_route: this.router.url || '',
      flushNow: true,
    });
  }

  trackKeyAction(action: string, options: ActivityTrackOptions = {}): void {
    this.track(action, {
      description: options.description || action,
      ...options,
      session_status: options.session_status || 'Active',
    });
  }

  /** Track allowlisted API call from HTTP interceptor. */
  trackApiCall(method: string, url: string): void {
    const mapped = this.mapApiToAction(method, url);
    if (!mapped) {
      return;
    }
    const meta = this.resolveRouteMeta();
    this.track(mapped.action, {
      module_name: meta.module_name,
      page_name: meta.page_name,
      page_route: meta.page_route,
      description: mapped.description,
      reference_type: 'api',
      reference_id: mapped.path,
    });
  }

  private mapApiToAction(
    method: string,
    url: string
  ): { action: string; description: string; path: string } | null {
    const m = String(method || 'GET').toUpperCase();
    const path = this.extractApiPath(url);
    if (!path) {
      return null;
    }

    // Never track activity/audit/auth session endpoints (handled separately / noisy)
    if (
      path.includes('/other/logactivity') ||
      path.includes('/other/logevent') ||
      path.includes('/auth/refresh') ||
      path.includes('/auth/login') ||
      path.includes('/auth1/login') ||
      path.includes('/auth1/logout') ||
      path.includes('/auth1/startsession') ||
      path.includes('/auth1/checksession') ||
      path.includes('/custom-login') ||
      path.includes('/gpsdata') ||
      path.includes('/other/gps')
    ) {
      return null;
    }

    // Allowlisted business API prefixes (V1)
    const allow =
      /\/(quotation|lead|customer|report|license|transaction|user|auth1|master|branch|creditnote|request|window|nocfoc|legal|tracking|activity|document|einvoicect|dtoc|verification|target|role)\//i.test(
        path
      ) ||
      /\/(quotation|lead|customer|report|license|transaction|user|auth1)\b/i.test(path);
    if (!allow) {
      return null;
    }

    let action = 'API_CALL';
    const p = path.toLowerCase();

    if (p.includes('genreport') || p.includes('download') || p.includes('licensedwl')) {
      action = 'CLICK_DOWNLOAD';
    } else if (p.includes('approve')) {
      action = 'CLICK_APPROVE';
    } else if (p.includes('reject')) {
      action = 'CLICK_REJECT';
    } else if (
      p.includes('addedit') ||
      p.includes('save') ||
      p.includes('create') ||
      p.includes('update') ||
      (m === 'POST' && (p.includes('/quotation') || p.includes('/lead') || p.includes('/customer')))
    ) {
      action = m === 'DELETE' ? 'CLICK_DELETE' : 'CLICK_SAVE';
    } else if (m === 'DELETE') {
      action = 'CLICK_DELETE';
    } else if (
      p.includes('search') ||
      p.includes('list') ||
      (m === 'GET' && (p.includes('filter') || p.includes('pageoffset')))
    ) {
      action = 'SEARCH';
    } else if (m === 'GET') {
      action = 'API_GET';
    } else if (m === 'POST' || m === 'PUT' || m === 'PATCH') {
      action = 'API_POST';
    }

    return {
      action,
      description: `${m} ${path}`,
      path,
    };
  }

  private extractApiPath(url: string): string {
    try {
      const u = String(url || '');
      // Skip Directus / V2 API noise
      if (u.includes('-v2-') || u.includes('/items/') || u.includes('/auth/refresh')) {
        return '';
      }
      const noQuery = u.split('?')[0];
      const idx = noQuery.indexOf('/v1/');
      if (idx >= 0) {
        return noQuery.substring(idx);
      }
      // Absolute or relative Restler paths like .../quotation/list
      const m = noQuery.match(/https?:\/\/[^/]+(\/.*)$/i);
      return m ? m[1] : noQuery.replace(/^https?:\/\/[^/]+/i, '');
    } catch {
      return '';
    }
  }

  private onNavigation(url: string): void {
    if (!url || this.isExcluded(url)) {
      return;
    }
    if (!this.apiV2.currentUserValue?.v1_data?.token) {
      return;
    }
    const path = this.normalizePath(url);
    if (!path || path === this.lastTrackedRoute) {
      return;
    }
    this.lastTrackedRoute = path;
    const menu = this.resolveMenu(path);
    this.track('PAGE_VIEW', {
      module_name: menu?.module_name || '',
      page_name: menu?.page_name || path,
      page_route: path,
      description: `Opened ${menu?.page_name || path}`,
      session_status: 'Active',
    });
  }

  private onVisibilityChange = (): void => {
    try {
      if (this.isExcluded(this.router.url)) {
        return;
      }
      if (!this.apiV2.currentUserValue?.v1_data?.token) {
        return;
      }

      if (document.visibilityState === 'hidden') {
        const leaveAt = Date.now().toString();
        sessionStorage.setItem(this.LEAVE_AT_KEY, leaveAt);
        this.track('APP_LEAVE', {
          description: 'User left PLUS application (tab/background)',
          session_status: 'Active',
          page_route: this.router.url || '',
          flushNow: true,
        });
        return;
      }

      if (document.visibilityState === 'visible') {
        let durationSec: number | undefined;
        const leaveAt = Number(sessionStorage.getItem(this.LEAVE_AT_KEY) || 0);
        if (leaveAt > 0) {
          durationSec = Math.max(0, Math.floor((Date.now() - leaveAt) / 1000));
          sessionStorage.removeItem(this.LEAVE_AT_KEY);
        }
        this.track('APP_RETURN', {
          description: 'User returned to PLUS application',
          session_status: 'Active',
          page_route: this.router.url || '',
          activity_duration_sec: durationSec,
          flushNow: true,
        });
      }
    } catch {
      /* ignore */
    }
  };

  private onPageHide = (): void => {
    try {
      this.flush();
    } catch {
      /* ignore */
    }
  };

  private resolveMenu(url: string): { module_name: string; page_name: string } | null {
    const path = this.normalizePath(url);
    if (!path || !this.menuIndex.length) {
      return null;
    }
    const hit = this.menuIndex.find(
      (m) => path === m.state || path.startsWith(m.state + '/')
    );
    return hit
      ? { module_name: hit.module_name, page_name: hit.page_name }
      : null;
  }

  private normalizePath(url: string): string {
    const bare = String(url || '').split('?')[0].split('#')[0];
    return bare.replace(/^\/+/, '').replace(/\/+$/, '');
  }

  private isExcluded(url: string): boolean {
    const path = '/' + this.normalizePath(url);
    return this.EXCLUDED_ROUTES.some((r) => path === r || path.startsWith(r + '/'));
  }

  private nowSql(): string {
    const d = new Date();
    const pad = (n: number) => (n < 10 ? '0' + n : '' + n);
    return (
      d.getFullYear() +
      '-' +
      pad(d.getMonth() + 1) +
      '-' +
      pad(d.getDate()) +
      ' ' +
      pad(d.getHours()) +
      ':' +
      pad(d.getMinutes()) +
      ':' +
      pad(d.getSeconds())
    );
  }
}
